import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import './Admin.css';

export default function AdminUsuarios() {
  const { usuario: usuarioLogado } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [canais, setCanais] = useState([]);
  const [modal, setModal] = useState(null);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const avatarRef = useRef(null);

  const carregar = useCallback(async () => {
    const [u, c] = await Promise.all([api.get('/usuarios'), api.get('/canais')]);
    setUsuarios(u.data);
    // Admin de canal só pode atribuir os canais que ele mesmo administra
    const canaisFiltrados = usuarioLogado?.perfil === 'admin'
      ? c.data
      : c.data.filter(canal => (usuarioLogado?.canaisAdmin || []).includes(canal.id));
    setCanais(canaisFiltrados);
  }, [usuarioLogado]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setForm({ nome: '', usuario: '', senha: '', perfil: 'atendente', canal_ids: [], canais_admin: [] });
    setUsuarioSelecionado(null);
    setAvatarPreview(null);
    setAvatarFile(null);
    setModal('form');
  };

  const abrirEditar = (u) => {
    setUsuarioSelecionado(u);
    setForm({
      nome: u.nome,
      usuario: u.usuario,
      senha: '',
      perfil: u.perfil,
      ativo: u.ativo,
      canal_ids: u.canais?.map(c => c.id) || [],
      canais_admin: u.canais?.filter(c => c.admin_canal).map(c => c.id) || [],
    });
    setAvatarPreview(u.avatar_url || null);
    setAvatarFile(null);
    setModal('form');
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const toggleCanal = (id) => {
    setForm(f => {
      const temAcesso = f.canal_ids.includes(id);
      return {
        ...f,
        canal_ids: temAcesso ? f.canal_ids.filter(x => x !== id) : [...f.canal_ids, id],
        // Se removeu o acesso ao canal, remove também a marca de admin desse canal
        canais_admin: temAcesso ? (f.canais_admin || []).filter(x => x !== id) : (f.canais_admin || []),
      };
    });
  };
  const toggleCanalAdmin = (id) => {
    setForm(f => ({
      ...f,
      canais_admin: (f.canais_admin || []).includes(id)
        ? f.canais_admin.filter(x => x !== id)
        : [...(f.canais_admin || []), id]
    }));
  };

  const salvar = async () => {
    if (!form.nome || !form.usuario) return toast.error('Nome e usuário são obrigatórios');
    if (!usuarioSelecionado && !form.senha) return toast.error('Senha obrigatória para novo usuário');

    setSalvando(true);
    try {
      const payload = { ...form };
      if (!payload.senha) delete payload.senha;
      // Transforma canal_ids + canais_admin no formato que o backend espera
      payload.canais = (payload.canal_ids || []).map(id => ({
        canal_id: id,
        admin_canal: (payload.canais_admin || []).includes(id),
      }));
      delete payload.canal_ids;
      delete payload.canais_admin;

      let userId;
      if (usuarioSelecionado) {
        await api.put(`/usuarios/${usuarioSelecionado.id}`, payload);
        userId = usuarioSelecionado.id;
        toast.success('Usuário atualizado!');
      } else {
        const { data } = await api.post('/usuarios', payload);
        userId = data.id;
        toast.success('Usuário criado!');
      }

      // Upload de avatar se selecionado
      if (avatarFile && userId) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        await api.post(`/usuarios/${userId}/avatar`, formData);
      }

      setModal(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const toggleAtivo = async (u) => {
    await api.put(`/usuarios/${u.id}`, { ativo: !u.ativo });
    toast.success(u.ativo ? 'Usuário desativado' : 'Usuário ativado');
    carregar();
  };

  const iniciais = (nome) => nome?.slice(0, 2).toUpperCase() || '??';

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Usuários</h1>
          <p>Gerencie atendentes e administradores</p>
        </div>
        <button className="btn-primary" onClick={abrirNovo}>+ Novo Usuário</button>
      </div>

      <div className="table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Usuário</th>
              <th>Perfil</th>
              <th>Canais vinculados</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map(u => (
              <tr key={u.id} className={!u.ativo ? 'inativo' : ''}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {u.avatar_url
                      ? <img src={u.avatar_url} alt={u.nome} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                      : <div className="table-avatar">{iniciais(u.nome)}</div>
                    }
                    {u.nome}
                  </div>
                </td>
                <td style={{ color: 'var(--texto-sec)' }}>@{u.usuario}</td>
                <td><span className={`perfil-badge ${u.perfil}`}>{u.perfil}</span></td>
                <td>
                  {u.perfil === 'admin'
                    ? <span style={{ color: 'var(--texto-sec)', fontSize: 12 }}>Todos os canais</span>
                    : u.canais?.length
                      ? u.canais.map(c => <span key={c.id} className="canal-tag">{c.nome}</span>)
                      : <span style={{ color: 'var(--texto-ter)', fontSize: 12 }}>Nenhum</span>
                  }
                </td>
                <td>
                  <span className={`status-pill ${u.ativo ? 'conectado' : 'desconectado'}`}>
                    {u.ativo ? '🟢 Ativo' : '⚫ Inativo'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-acao-sm" onClick={() => abrirEditar(u)}>✏️ Editar</button>
                    <button className="btn-acao-sm" onClick={() => toggleAtivo(u)}>
                      {u.ativo ? '🔒 Desativar' : '✅ Ativar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === 'form' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{usuarioSelecionado ? `Editar — ${usuarioSelecionado.nome}` : 'Novo Usuário'}</h3>
              <button onClick={() => setModal(null)}>✕</button>
            </div>

            {/* Avatar */}
            <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => avatarRef.current?.click()}>
                {avatarPreview
                  ? <img src={avatarPreview} alt="Avatar" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--cinza-borda)' }} />
                  : <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#EEEDFE', color: '#3C3489', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 600, border: '2px dashed var(--cinza-borda)' }}>
                      {form.nome ? iniciais(form.nome) : '👤'}
                    </div>
                }
                <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#1D9E75', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>📷</div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Foto do atendente</div>
                <button className="btn-secondary" style={{ fontSize: 11, marginTop: 4, padding: '4px 10px' }} onClick={() => avatarRef.current?.click()}>
                  {avatarPreview ? 'Trocar foto' : 'Adicionar foto'}
                </button>
              </div>
              <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>

            <div className="form-section">
              <div className="form-row">
                <div className="field">
                  <label>Nome completo</label>
                  <input value={form.nome || ''} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="João Silva" />
                </div>
                <div className="field">
                  <label>Usuário (login)</label>
                  <input value={form.usuario || ''} onChange={e => setForm(f => ({ ...f, usuario: e.target.value.toLowerCase() }))} placeholder="joao.silva" />
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label>{usuarioSelecionado ? 'Nova senha (deixe vazio para não alterar)' : 'Senha'}</label>
                  <input type="password" value={form.senha || ''} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} placeholder="••••••••" />
                </div>
                <div className="field">
                  <label>Perfil</label>
                  <select value={form.perfil || 'atendente'} onChange={e => setForm(f => ({ ...f, perfil: e.target.value }))}>
                    <option value="atendente">Atendente</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              </div>
            </div>

            {form.perfil === 'atendente' && (
              <div className="form-section">
                <div className="section-label">Canais que este atendente pode ver</div>
                <div className="atendentes-lista">
                  {canais.map(c => (
                    <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                      <label className="atendente-check">
                        <input type="checkbox"
                          checked={form.canal_ids?.includes(c.id)}
                          onChange={() => toggleCanal(c.id)} />
                        <div className="at-avatar" style={{ background: '#E1F5EE', color: '#0F6E56' }}>📱</div>
                        <div>
                          <div className="at-nome">{c.nome}</div>
                          <div className="at-user">{c.numero}</div>
                        </div>
                      </label>
                      {form.canal_ids?.includes(c.id) && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 40, fontSize: 12, color: 'var(--texto-sec)', cursor: 'pointer' }}>
                          <input type="checkbox"
                            checked={form.canais_admin?.includes(c.id) || false}
                            onChange={() => toggleCanalAdmin(c.id)} />
                          Também é administrador deste canal
                        </label>
                      )}
                    </div>
                  ))}
                  {canais.length === 0 && <p style={{ color: 'var(--texto-sec)', fontSize: 13 }}>Nenhum canal cadastrado.</p>}
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
